import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
import { getEventByIdAdmin, getEventSeatMap, saveSeatPlan } from "../../api/plan";
import { getVenueByIdAdmin } from "../../api/venue";
import { MapViewport, Shape, chairPositions, padNumber } from "../../components/SeatMap";
import { currentSeatPlan, planFromTicketTypeSettings, seatPlanKey, ticketTypeStyles } from "../../components/SeatMap/pricing";
import { MAX_SEATS_PER_ORDER } from "../../components/SeatPickerModal";

// Seat plan editor: every seat is sold by one ticket type (Standart, VIP1, ...) or not on sale.
// Pick a ticket type as the brush, then click or drag over seats, or use the zone/row buttons.

const ERASER = "";

// Compare plans regardless of key order
const canonical = (plan) => JSON.stringify(Object.keys(plan).sort().map((key) => [key, plan[key]]));

const PlanSeat = ({ zone, seat, size, typeId, style, capacity, isSold, isSelected, onPaintStart, onPaintEnter }) => {
  const key = seatPlanKey(zone.code, seat.number);
  const isTable = zone.kind === "tables";
  const rect = isSelected
    ? "fill-pink-600 stroke-pink-600"
    : typeId && style ? `${style.fill} ${style.seat}` : "fill-white stroke-surface-300";
  const text = isSelected ? "fill-white" : typeId && style ? style.text : "fill-surface-400";
  return (
    <g
      transform={`translate(${seat.x - size / 2} ${seat.y - size / 2})`}
      onPointerDown={(e) => {
        e.preventDefault();
        onPaintStart(key);
      }}
      onPointerEnter={() => onPaintEnter(key)}
      className={`cursor-pointer ${isSold ? "opacity-50" : ""}`}
      data-seat={key}
    >
      <title>{`${zone.name || zone.code}, ${isTable ? "masa" : "yer"} ${seat.number}${isSold ? ", satılıb" : ""}${isSelected ? ", seçilib" : ""}`}</title>
      {isTable &&
        chairPositions(capacity || 0, size).map((chair, i) => (
          <circle key={i} cx={chair.cx} cy={chair.cy} r={size * 0.1} className="fill-surface-400" />
        ))}
      <rect width={size} height={size} rx={size * 0.2} strokeWidth={2} className={rect} />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.42}
        className={`font-semibold pointer-events-none select-none ${text}`}
      >
        {padNumber(seat.number)}
      </text>
    </g>
  );
};

const SeatPlanPage = () => {
  const { eventId } = useParams();
  const { isAdmin, canIssueTickets } = useRole();
  const [event, setEvent] = useState(null);
  const [venue, setVenue] = useState(null);
  const [takenSeats, setTakenSeats] = useState([]);
  const [plan, setPlan] = useState({});
  const [savedPlan, setSavedPlan] = useState(null);
  const [tool, setTool] = useState(ERASER);
  // "paint" assigns ticket types to seats; "sell" picks seats for a new ticket
  const [mode, setMode] = useState(isAdmin ? "paint" : "sell");
  const [pickedKeys, setPickedKeys] = useState([]);
  const [pickNotice, setPickNotice] = useState(null);
  const [status, setStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const paintingRef = useRef(false);
  const initialPlanRef = useRef(null);
  const navigate = useNavigate();

  const ticketTypes = useMemo(() => event?.ticketTypes || [], [event]);
  const styles = useMemo(() => ticketTypeStyles(ticketTypes), [ticketTypes]);
  const sortedTypes = useMemo(
    () => [...ticketTypes].sort((a, b) => (b.price || 0) - (a.price || 0)),
    [ticketTypes]
  );
  const soldKeys = useMemo(() => new Set(takenSeats.map(({ zone, number }) => seatPlanKey(zone, number))), [takenSeats]);
  const isDirty = savedPlan === null || canonical(plan) !== savedPlan;

  useEffect(() => {
    const load = async () => {
      const eventRes = await getEventByIdAdmin(eventId);
      const data = eventRes?.data?.data;
      setEvent(data);
      if (data?.venueId) {
        const venueRes = await getVenueByIdAdmin(data.venueId);
        const loadedVenue = venueRes?.data?.data;
        setVenue(loadedVenue);
        // Without a saved plan, start from what buyers get today (zone/row ticket type settings);
        // seats of deleted or inactive ticket types are left out
        const initialPlan = currentSeatPlan(loadedVenue, data.ticketTypes || [], data.seatPlan);
        setPlan(initialPlan);
        initialPlanRef.current = canonical(initialPlan);
        setSavedPlan(data.seatPlan ? canonical(data.seatPlan) : null);
        getEventSeatMap(eventId)
          .then((res) => setTakenSeats(res?.data?.data?.takenSeats || []))
          .catch(() => setTakenSeats([]));
      }
      const firstType = [...(data?.ticketTypes || [])].sort((a, b) => (b.price || 0) - (a.price || 0))[0];
      setTool(firstType?._id || ERASER);
      setIsLoading(false);
    };
    load();
  }, [eventId]);

  // Stop painting wherever the pointer is released
  useEffect(() => {
    const stop = () => {
      paintingRef.current = false;
    };
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    return () => {
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
  }, []);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!isDirty || isLoading) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty, isLoading]);

  const apply = useCallback(
    (keys) => {
      setStatus(null);
      setPlan((previous) => {
        const next = { ...previous };
        keys.forEach((key) => {
          if (tool) next[key] = tool;
          else delete next[key];
        });
        return next;
      });
    },
    [tool]
  );

  // One order is paid for as one ticket type, and sold seats are gone already
  const pickSeat = (key) => {
    setPickNotice(null);
    if (pickedKeys.includes(key)) {
      setPickedKeys(pickedKeys.filter((picked) => picked !== key));
      return;
    }
    if (soldKeys.has(key)) {
      setPickNotice("Bu yer artıq satılıb.");
      return;
    }
    if (!plan[key]) {
      setPickNotice("Bu yer satışda deyil. Əvvəlcə ona bilet növü təyin edin.");
      return;
    }
    if (pickedKeys.length >= MAX_SEATS_PER_ORDER) {
      setPickNotice(`Bir biletə ən çox ${MAX_SEATS_PER_ORDER} yer seçmək olar.`);
      return;
    }
    if (pickedKeys.length && plan[pickedKeys[0]] !== plan[key]) {
      setPickNotice("Bir biletdə yalnız eyni bilet növündən yerlər ola bilər.");
      return;
    }
    setPickedKeys([...pickedKeys, key]);
  };

  const onPaintStart = (key) => {
    if (mode === "sell") {
      if (canIssueTickets) pickSeat(key);
      return;
    }
    if (!isAdmin) return;
    paintingRef.current = true;
    apply([key]);
  };
  const onPaintEnter = (key) => {
    if (isAdmin && mode === "paint" && paintingRef.current) apply([key]);
  };

  // The plan must be saved first, or the seats would be sold by what the server
  // still has rather than by what is on screen.
  const hasUnsavedEdits = initialPlanRef.current !== null && canonical(plan) !== initialPlanRef.current;

  const createTicketFromSeats = () => {
    const seats = pickedKeys.map((key) => {
      const cut = key.lastIndexOf("-");
      return { zone: key.slice(0, cut), number: Number(key.slice(cut + 1)), ticketTypeId: plan[key] };
    });
    navigate(`/dashboard/${eventId}/tickets/new`, { state: { seats } });
  };

  const zoneKeys = (zone, sectionCode) =>
    zone.seats.filter((seat) => !sectionCode || seat.section === sectionCode).map((seat) => seatPlanKey(zone.code, seat.number));

  const counts = useMemo(() => {
    const result = {};
    Object.values(plan).forEach((typeId) => {
      result[typeId] = (result[typeId] || 0) + 1;
    });
    return result;
  }, [plan]);
  const totalSeats = (venue?.zones || []).reduce((sum, zone) => sum + zone.seats.length, 0);
  const unassigned = totalSeats - Object.keys(plan).length;

  const handleSave = async () => {
    setIsSaving(true);
    setStatus(null);
    try {
      const res = await saveSeatPlan(eventId, plan);
      const saved = res?.data?.data || {};
      setPlan({ ...saved });
      setSavedPlan(canonical(saved));
      setEvent((previous) => ({ ...previous, seatPlan: saved }));
      setStatus({ type: "success", message: "Yer planı yadda saxlandı. Satış bu plana əsasən aparılır." });
    } catch (error) {
      setStatus({ type: "error", message: error?.response?.data?.message || "Yadda saxlamaq olmadı." });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePlan = async () => {
    if (!window.confirm("Yer planı silinsin? Satış yenidən bilet növlərinin zona ayarlarına əsasən aparılacaq.")) return;
    setIsSaving(true);
    try {
      await saveSeatPlan(eventId, null);
      const fallback = planFromTicketTypeSettings(venue, ticketTypes);
      setPlan(fallback);
      setSavedPlan(null);
      setEvent((previous) => ({ ...previous, seatPlan: undefined }));
      setStatus({ type: "success", message: "Yer planı silindi." });
    } catch (error) {
      setStatus({ type: "error", message: error?.response?.data?.message || "Silmək olmadı." });
    } finally {
      setIsSaving(false);
    }
  };

  const seatSize = venue?.seatSize || 30;

  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-7xl mx-auto mt-6 lg:mt-10 px-2">
        <h2 className="text-2xl font-bold mb-1">🪑 Yer planı</h2>
        <p className="text-sm text-gray-500 mb-6">
          {isAdmin
            ? "Hər yerin hansı bilet növü ilə (məs. Standart, VIP1, VIP2) satıldığını təyin edin. Plan qeyd olunmayan yerlər satışda olmur."
            : canIssueTickets
              ? "Hər yerin hansı bilet növü ilə satıldığını göstərir. Sxemdən yer seçib dəvətnamə və ya əməkdaşlıq bileti yaza bilərsiniz; planın özünü yalnız admin dəyişir."
              : "Hər yerin hansı bilet növü ilə satıldığını göstərir. Dəyişiklik etmək üçün tədbir adminine müraciət edin."}
        </p>

        {isLoading ? (
          <div className="h-96 bg-gray-100 rounded-xl animate-pulse" />
        ) : !event?.venueId ? (
          <div className="bg-white p-6 rounded-xl shadow text-sm text-gray-700">
            Əvvəlcə <Link to={`/dashboard/${eventId}/settings`} className="text-blue-600 hover:underline">Parametrlər</Link> bölməsində zal sxemi seçin.
          </div>
        ) : ticketTypes.length === 0 ? (
          <div className="bg-white p-6 rounded-xl shadow text-sm text-gray-700">
            Əvvəlcə <Link to={`/dashboard/${eventId}/ticket-types`} className="text-blue-600 hover:underline">Bilet Növləri</Link> bölməsində
            bilet növlərini (məs. Standart, VIP1, VIP2) qiymətləri ilə yaradın.
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
            {/* Map */}
            <div className="bg-white p-4 rounded-xl shadow">
              <MapViewport
                venue={venue}
                className="h-[70vh]"
                // Painting needs the drag to itself; picking lets the map scroll
                canvasStyle={mode === "paint" ? { touchAction: "none" } : undefined}
                hint={({ isCompact, zoomIndex, tapZoomIndex }) =>
                  (mode === "sell" && isCompact && zoomIndex < tapZoomIndex
                    ? "Yaxınlaşdırmaq üçün sxemə toxunun"
                    : "")}
              >
                {({ isCompact, zoomIndex, zoomTo, tapZoomIndex }) => {
                  // Picking a seat on a phone: the first tap zooms in on it,
                  // because a seat drawn at 100% is smaller than a fingertip.
                  const handleStart = (key, seat) => {
                    if (mode === "sell" && isCompact && zoomIndex < tapZoomIndex) {
                      zoomTo(tapZoomIndex, { x: seat.x, y: seat.y });
                      return;
                    }
                    onPaintStart(key);
                  };

                  return (
                    <>
                      {(venue.shapes || []).map((shape, i) => (
                        <Shape key={i} shape={shape} />
                      ))}
                      {venue.zones.map((zone) =>
                        zone.seats.map((seat) => {
                          const key = seatPlanKey(zone.code, seat.number);
                          const typeId = plan[key];
                          return (
                            <PlanSeat
                              key={key}
                              zone={zone}
                              seat={seat}
                              size={seat.size || seatSize}
                              typeId={typeId}
                              style={styles[typeId]}
                              capacity={ticketTypes.find(({ _id }) => _id === typeId)?.capacity}
                              isSold={soldKeys.has(key)}
                              isSelected={pickedKeys.includes(key)}
                              onPaintStart={(paintKey) => handleStart(paintKey, seat)}
                              onPaintEnter={onPaintEnter}
                            />
                          );
                        })
                      )}
                    </>
                  );
                }}
              </MapViewport>
            </div>

            {/* Tools */}
            <div className="space-y-4 lg:sticky lg:top-24">
              {canIssueTickets && (
                <div className="bg-white p-4 rounded-xl shadow">
                  {isAdmin ? (
                  <div className="flex gap-2" role="tablist" aria-label="Rejim">
                    {[["paint", "🎨 Plan qur"], ["sell", "🎟️ Bilet yarat"]].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={mode === value}
                        onClick={() => {
                          setMode(value);
                          setPickedKeys([]);
                          setPickNotice(null);
                        }}
                        className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                          mode === value ? "border-black bg-gray-900 text-white" : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  ) : (
                    <h3 className="text-sm font-semibold">🎟️ Bilet yarat</h3>
                  )}
                  {mode === "sell" && (
                    <div className="mt-3">
                      {hasUnsavedEdits ? (
                        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                          Planda yadda saxlanılmamış dəyişikliklər var. Bilet yaratmazdan əvvəl planı yadda saxlayın.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">
                          Sxemdən yer seçin (ən çox {MAX_SEATS_PER_ORDER}), sonra «Bilet yarat» düyməsinə basın.
                        </p>
                      )}
                      {pickedKeys.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {pickedKeys.map((key) => (
                            <li key={key} className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-gray-900">{key}</span>
                              <button
                                type="button"
                                onClick={() => pickSeat(key)}
                                aria-label={`${key} seçimini ləğv et`}
                                className="ml-auto text-gray-400 hover:text-red-600"
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {pickNotice && <p className="mt-2 text-xs text-amber-700">{pickNotice}</p>}
                      <button
                        type="button"
                        onClick={createTicketFromSeats}
                        disabled={!pickedKeys.length || hasUnsavedEdits}
                        className="mt-3 w-full rounded-lg bg-black px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:bg-gray-300"
                      >
                        {pickedKeys.length ? `${pickedKeys.length} yer üçün bilet yarat` : "Bilet yarat"}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="bg-white p-4 rounded-xl shadow">
                <h3 className="text-sm font-semibold mb-3">{isAdmin ? "1. Bilet növü seçin" : "Bilet növləri"}</h3>
                <div className="space-y-2" role="radiogroup" aria-label="Bilet növü">
                  {sortedTypes.map((type) => (
                    <button
                      key={type._id}
                      type="button"
                      role="radio"
                      aria-checked={tool === type._id}
                      onClick={() => setTool(type._id)}
                      className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition ${
                        tool === type._id ? "border-black ring-1 ring-black bg-gray-50" : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded border-2 ${styles[type._id]?.swatch} ${styles[type._id]?.solid}`} />
                      <span className="font-medium">{type.name?.az}</span>
                      <span className="text-gray-500">{type.price}₼</span>
                      <span className="ml-auto text-xs text-gray-500 tabular-nums">{counts[type._id] || 0} yer</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={tool === ERASER}
                    onClick={() => setTool(ERASER)}
                    className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition ${
                      tool === ERASER ? "border-black ring-1 ring-black bg-gray-50" : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <span className="w-4 h-4 rounded border-2 border-gray-300 bg-white" />
                    <span className="font-medium">Satışda deyil</span>
                    <span className="ml-auto text-xs text-gray-500 tabular-nums">{unassigned} yer</span>
                  </button>
                </div>
              </div>

              {isAdmin && mode === "paint" && (
                <>
              <div className="bg-white p-4 rounded-xl shadow">
                <h3 className="text-sm font-semibold mb-1">2. Yerləri təyin edin</h3>
                <p className="text-xs text-gray-500 mb-3">Sxemdə yerə klikləyin və ya siçanı basılı saxlayıb yerlərin üzərindən keçirin.</p>
                <div className="space-y-3">
                  {venue.zones.map((zone) => (
                    <div key={zone.code}>
                      <div className="text-xs font-semibold text-gray-700 mb-1">{zone.name || zone.code}</div>
                      <div className="flex flex-wrap gap-1.5" role="group" aria-label={zone.name || zone.code}>
                        <button type="button" onClick={() => apply(zoneKeys(zone))} className="text-xs rounded border border-gray-300 px-2 py-1 hover:bg-gray-100">
                          Bütün zona
                        </button>
                        {(zone.sections || []).map((section) => (
                          <button
                            key={section.code}
                            type="button"
                            onClick={() => apply(zoneKeys(zone, section.code))}
                            className="text-xs rounded border border-gray-300 px-2 py-1 hover:bg-gray-100"
                          >
                            {section.name || section.code}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => apply(venue.zones.flatMap((zone) => zoneKeys(zone)))}
                    className="text-xs rounded border border-gray-300 px-2 py-1 hover:bg-gray-100"
                  >
                    Bütün zal
                  </button>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl shadow space-y-3">
                {!event.seatPlan && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                    Bu tədbirin hələ yer planı yoxdur. Sxem hazırkı ayarlardan hazırlanıb; yadda saxlayana qədər satış köhnə qaydada davam edir.
                  </p>
                )}
                {soldKeys.size > 0 && (
                  <p className="text-xs text-gray-500">Solğun yerlər artıq satılıb; onların bileti və qiyməti dəyişmir.</p>
                )}
                {status && (
                  <p className={`text-sm ${status.type === "success" ? "text-emerald-700" : "text-red-600"}`}>{status.message}</p>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
                  className="w-full bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400"
                >
                  {isSaving ? "Yadda saxlanılır..." : isDirty ? "Yadda saxla" : "Yadda saxlanılıb"}
                </button>
                {event.seatPlan && (
                  <button type="button" onClick={handleRemovePlan} disabled={isSaving} className="w-full text-xs text-red-600 hover:underline">
                    Yer planını sil
                  </button>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SeatPlanPage;
